import { useEffect, useState } from "react";
import api, { formatError } from "@/api/client";
import { toast } from "sonner";
import { Plus, Trash2, Mail } from "lucide-react";
import { useAuthStore } from "@/store/auth";

export default function OrgSettingsPage() {
  const me = useAuthStore((s) => s.user);
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const [org, setOrg] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("editor");
  const [profile, setProfile] = useState({ full_name: "", avatar_url: "" });

  const load = async () => {
    const [o, m, i] = await Promise.all([
      api.get("/org"),
      api.get("/org/members"),
      api.get("/org/invitations"),
    ]);
    setOrg(o.data);
    setOrgName(o.data.name);
    setMembers(m.data);
    setInvitations(i.data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (me) setProfile({ full_name: me.full_name || "", avatar_url: me.avatar_url || "" }); }, [me]);

  const saveOrg = async () => {
    try { await api.put("/org", { name: orgName }); toast.success("Saved"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const saveProfile = async () => {
    try { await updateProfile(profile); toast.success("Profile updated"); }
    catch (e) { toast.error(formatError(e)); }
  };

  const invite = async (e) => {
    e.preventDefault();
    try {
      await api.post("/org/members/invite", { email: inviteEmail, role: inviteRole });
      toast.success("Invite sent");
      setInviteEmail("");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const removeMember = async (id) => {
    if (!confirm("Remove this member?")) return;
    await api.delete(`/org/members/${id}`);
    load();
  };

  const cancelInvite = async (id) => {
    await api.delete(`/org/invitations/${id}`);
    load();
  };

  if (!org) return <div className="p-8 text-zinc-500">Loading…</div>;

  return (
    <div className="p-6 sm:p-10 max-w-4xl space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 font-bold">Organization</div>
        <h1 className="text-3xl font-black tracking-tight">Settings</h1>
      </div>

      <section className="bg-white border border-zinc-200 rounded-sm p-6">
        <h2 className="font-bold mb-4">Your profile</h2>
        <div className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-bold mb-1.5">Full name</label>
            <input value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
              data-testid="profile-name"
              className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm" />
          </div>
          <button onClick={saveProfile} data-testid="profile-save"
            className="bg-[#41B5E6] hover:bg-[#2A9BCC] text-white px-4 py-2 rounded-sm font-semibold text-sm">Update profile</button>
        </div>
      </section>

      <section className="bg-white border border-zinc-200 rounded-sm p-6">
        <h2 className="font-bold mb-4">Organization</h2>
        <div className="flex gap-2 max-w-md">
          <input value={orgName} onChange={(e) => setOrgName(e.target.value)}
            data-testid="org-name"
            className="flex-1 px-3 py-2.5 border border-zinc-300 rounded-sm text-sm" />
          <button onClick={saveOrg} className="bg-[#41B5E6] hover:bg-[#2A9BCC] text-white px-4 py-2 rounded-sm font-semibold text-sm">Save</button>
        </div>
        <div className="text-xs text-zinc-500 mt-3">Plan: <span className="font-bold uppercase">{org.plan}</span></div>
      </section>

      <section className="bg-white border border-zinc-200 rounded-sm p-6">
        <h2 className="font-bold mb-4">Invite a teammate</h2>
        <form onSubmit={invite} className="flex gap-2" data-testid="invite-form">
          <input type="email" required value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="email@example.com"
            data-testid="invite-email"
            className="flex-1 px-3 py-2.5 border border-zinc-300 rounded-sm text-sm" />
          <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
            data-testid="invite-role"
            className="px-3 py-2.5 border border-zinc-300 rounded-sm text-sm">
            <option value="admin">Admin</option>
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
          </select>
          <button type="submit" data-testid="invite-submit"
            className="bg-[#41B5E6] hover:bg-[#2A9BCC] text-white px-4 py-2 rounded-sm font-semibold text-sm flex items-center gap-2">
            <Mail className="w-4 h-4" /> Invite
          </button>
        </form>
      </section>

      <section className="bg-white border border-zinc-200 rounded-sm">
        <div className="p-5 border-b font-bold">Members ({members.length})</div>
        <div className="divide-y divide-zinc-100">
          {members.map((m) => {
            const memberRole = org.members?.find((x) => x.user_id === m.id)?.role || m.role;
            const isOwner = m.id === org.owner_id;
            return (
              <div key={m.id} className="p-4 flex items-center gap-3">
                <div className="w-8 h-8 bg-zinc-200 rounded-full flex items-center justify-center text-xs font-bold">
                  {m.full_name?.charAt(0) || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{m.full_name}</div>
                  <div className="text-xs text-zinc-500 truncate">{m.email}</div>
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm bg-zinc-100">{memberRole}</span>
                {!isOwner && me?.id === org.owner_id && (
                  <button onClick={() => removeMember(m.id)} className="text-zinc-400 hover:text-rose-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {invitations.length > 0 && (
        <section className="bg-white border border-zinc-200 rounded-sm">
          <div className="p-5 border-b font-bold">Pending invitations ({invitations.length})</div>
          <div className="divide-y divide-zinc-100">
            {invitations.map((i) => (
              <div key={i.id} className="p-4 flex items-center gap-3">
                <Mail className="w-4 h-4 text-zinc-400" />
                <div className="flex-1">
                  <div className="font-semibold text-sm">{i.email}</div>
                  <div className="text-xs text-zinc-500">Invited as {i.role} · {new Date(i.created_at).toLocaleString()}</div>
                </div>
                <button onClick={() => cancelInvite(i.id)} className="text-xs text-rose-600 hover:underline">Cancel</button>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
